import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Layout from 'components/sales/layout/layout';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import {
  getRegularizationRequestDetails,
  updateRegularizationRequest,
} from 'services/darwinRecord/darwinRecordApi';
import { LIST_STATUS, REGULARIZATION_STATUS } from './constants';
import { formatDisplayDate, formatTime, parseQueryParams } from './darwinRecordUtils';
import { validateRejectRegularization } from './darwinRecordValidation';
import './darwinRecord.css';

const DetailField = ({ label, value }) => (
  <div className="darwin-details-field">
    <span className="darwin-details-field__label">{label}</span>
    <span className="darwin-details-field__value">{value || '—'}</span>
  </div>
);

const DarwinRegularizationDetails = ({ history, location }) => {
  const { baseUrl } = useContext(BaseUrl);
  const query = parseQueryParams(location?.search);
  const id = query.id;
  const requestType = (query.type || 'REGULARIZATION').toUpperCase();
  const listStatus = query.list_status || LIST_STATUS.PENDING;
  const mode = query.mode || 'view';

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [managerRemark, setManagerRemark] = useState('');
  const [details, setDetails] = useState(null);
  const isWooo =
    requestType === 'WOOO' || String(details?.type || '').toUpperCase() === 'WOOO';

  const backPath =
    listStatus === LIST_STATUS.PENDING
      ? `${baseUrl}/darwin-record/regularizations/manager`
      : `${baseUrl}/darwin-record/regularizations/employee`;

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) {
        setIsLoading(false);
        toast.error('Request id is missing');
        return;
      }

      setIsLoading(true);
      const { data, error } = await getRegularizationRequestDetails({
        id,
        request_type: requestType,
        list_status: listStatus,
      });

      if (error) {
        setDetails(null);
        toast.error(error);
      } else {
        setDetails(data);
      }
      setIsLoading(false);
    };

    fetchDetails();
  }, [id, requestType]);

  const handleUpdate = async (status) => {
    if (!id || !details) return;

    if (status === 2) {
      const validation = validateRejectRegularization(managerRemark);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    }

    setIsSubmitting(true);
    const { error } = await updateRegularizationRequest({
      id,
      type: details.type || requestType,
      status,
      manager_remark: managerRemark.trim() || undefined,
    });
    setIsSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(
      status === 1 ? 'Request approved successfully' : 'Request rejected successfully'
    );
    setTimeout(() => {
      history.push(backPath);
    }, 500);
  };

  const canAct = mode === 'action' && details?.status === 0 && !isLoading && !!details;
  const statusMeta = REGULARIZATION_STATUS[details?.status] || {
    text: 'Unknown',
    className: 'darwin-status-default',
  };

  const pageTitle = canAct ? 'Regularization Request' : 'View Details';

  const hasDisplayValue = (value) => {
    const text = String(value ?? '').trim();
    return Boolean(text) && text.toLowerCase() !== 'null' && text !== '—';
  };

  const isLocationTypeEnum = (value) =>
    ['OUT_OF_OFFICE', 'IN_OFFICE', 'SITE', 'NOT_SITE', 'OTHER'].includes(
      String(value || '').toUpperCase()
    );

  const showLocationOfWork =
    hasDisplayValue(details?.locationOfWork) &&
    !isLocationTypeEnum(details?.locationOfWork);

  const showManagerRemark =
    !canAct && !isWooo && hasDisplayValue(details?.managerRemark);

  const hasExtraFields = !isWooo || showLocationOfWork;

  return (
    <Layout
      title={pageTitle}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={backPath}
    >
      <div className="darwin-record-page darwin-regularization-details">
        {!details && !isLoading ? (
          <div className="darwin-empty-state">
            <p className="darwin-empty-state__title">Unable to load request details</p>
          </div>
        ) : (
          <div className="darwin-details-panel">
            <div className="darwin-details-panel__header">
              <div className="darwin-details-panel__identity">
                <h2 className="darwin-details-panel__name">
                  {details?.requestorName || '—'}
                </h2>
                <p className="darwin-details-panel__email">
                  {details?.requestorEmail || '—'}
                </p>
              </div>
              <span className={`darwin-status-badge ${statusMeta.className}`}>
                {statusMeta.text}
              </span>
            </div>

            <div className="darwin-details-panel__body">
              <div className="darwin-details-meta-grid">
                <DetailField
                  label="Date"
                  value={formatDisplayDate(details?.requestedForDate)}
                />
                <DetailField label="From" value={formatTime(details?.checkInTime)} />
                <DetailField label="To" value={formatTime(details?.checkOutTime)} />
              </div>

              {hasExtraFields ? (
                <div className="darwin-details-extra-grid">
                  {!isWooo ? (
                    <DetailField
                      label="Reason for missed attendance"
                      value={details?.reasonForMissedAttendance}
                    />
                  ) : null}
                  {showLocationOfWork ? (
                    <DetailField label="Location of work" value={details.locationOfWork} />
                  ) : null}
                </div>
              ) : null}

              <div className="darwin-details-section">
                <span className="darwin-details-section__label">
                  {isWooo ? 'Check-in remark' : 'Reason'}
                </span>
                <div className="darwin-details-text-block">
                  {(isWooo ? details?.checkInRemark : details?.message) || '—'}
                </div>
              </div>

              {isWooo ? (
                <div className="darwin-details-section">
                  <span className="darwin-details-section__label">
                    Check-out remark
                  </span>
                  <div className="darwin-details-text-block">
                    {details?.checkOutRemark || '—'}
                  </div>
                </div>
              ) : null}

              <div className="darwin-details-section">
                <span className="darwin-details-section__label">Attachment</span>
                {details?.attachment ? (
                  <div className="darwin-details-attachment">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="darwin-details-download-btn"
                      onClick={() => window.open(details.attachment, '_blank')}
                    >
                      Download attachment
                    </Button>
                  </div>
                ) : (
                  <div className="darwin-details-attachment darwin-details-attachment--empty">
                    No attachment available
                  </div>
                )}
              </div>

              {showManagerRemark ? (
                <div className="darwin-details-section">
                  <span className="darwin-details-section__label">Manager remark</span>
                  {details?.status === 2 ? (
                    <div className="darwin-remark-box">
                      <p className="darwin-remark-box__text mb-0">{details.managerRemark}</p>
                    </div>
                  ) : (
                    <div className="darwin-details-text-block">{details.managerRemark}</div>
                  )}
                </div>
              ) : null}

              {canAct ? (
                <div className="darwin-details-section">
                  <label className="darwin-details-section__label" htmlFor="manager-remark">
                    Manager remark
                  </label>
                  <textarea
                    id="manager-remark"
                    className="darwin-details-textarea"
                    rows={3}
                    value={managerRemark}
                    onChange={(event) => setManagerRemark(event.target.value)}
                    placeholder="Required when rejecting"
                  />
                </div>
              ) : null}
            </div>

            {canAct ? (
              <div className="darwin-details-panel__footer">
                <Button
                  variant="outline-danger"
                  className="darwin-details-action-btn darwin-details-action-btn--reject"
                  disabled={isSubmitting}
                  onClick={() => handleUpdate(2)}
                >
                  {isSubmitting ? 'Processing...' : 'Reject'}
                </Button>
                <Button
                  variant="primary"
                  className="darwin-details-action-btn darwin-details-action-btn--approve"
                  disabled={isSubmitting}
                  onClick={() => handleUpdate(1)}
                >
                  {isSubmitting ? 'Processing...' : 'Approve'}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DarwinRegularizationDetails;
